import type {
	FullCourseLevel,
	FullCourseOffering,
	FullCourseOfferings,
} from "@repo/sanity-utils/query-types";
import type {CampusLocation} from "@repo/actions/AcademyApi/GetCampuses";
import {formatOfferingPrice, getOfferingPrice} from "@/lib/offeringPrice";
import {getKeyedCopyText, type KeyedCopyEntry} from "@repo/utils/keyedCopy";
import {
	resolveInPersonBillingType,
	type InPersonBillingType,
} from "@/lib/learningFormatData";
import {
	BEAST_ACADEMY_ENROLL_URL,
	BEAST_ACADEMY_MODALITY_SLUG,
} from "@/lib/const";
import {CTA_COPY} from "../CTACopyConstants";
import {appendScheduleAnchor} from "../courseUrl";
import {
	getCourseOfferingScheduleLinks,
	resolveScheduleLinkTrackingEvent,
	type ScheduleLinkTrackingEvent,
	type SecondaryCTAScheduleLink,
} from "../SecondaryCTA/SecondaryCTAUtils";

/**
 * Main text of the call to action
 */
export function getPrimaryCTAHeader(
	offering: FullCourseOffering,
	campuses: CampusLocation[],
	selectedCampusId: string | null,
	keyedCopy: KeyedCopyEntry[] = [],
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"],
	courseTitle = ""
) {
	// Special case for versions sequence type
	if (sequenceType === "versions") {
		const headerTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.primary.header.versions.key) ||
			CTA_COPY.primary.header.versions.fallback;
		return headerTemplate.replace("{{courseTitle}}", courseTitle);
	}

	// Otherwise this is based on modality
	switch (offering.modality.slug.current) {
		case "in-person": {
			const selectedCampus = campuses.find(
				(campus) => campus.campus_id === selectedCampusId
			);

			// Header when there is no selected campus (Should not happen)
			if (!selectedCampus) {
				return (
					getKeyedCopyText(
						keyedCopy,
						CTA_COPY.primary.header.noneInPerson.key
					) || CTA_COPY.primary.header.noneInPerson.fallback
				);
			}

			// Header when there is a selected campus
			const headerTemplate =
				getKeyedCopyText(keyedCopy, CTA_COPY.primary.header.inPerson.key) ||
				CTA_COPY.primary.header.inPerson.fallback;
			return headerTemplate.replace(
				"{{courseTitle}}",
				selectedCampus.short_name
			);
		}
		case "video-based":
		case "chat-based":
		case "self-paced": {
			return (
				getKeyedCopyText(keyedCopy, CTA_COPY.primary.header.default.key) ||
				CTA_COPY.primary.header.default.fallback
			);
		}
		default:
			return "";
	}
}

export function getPrimaryCTAStepTag(keyedCopy: KeyedCopyEntry[] = []) {
	return (
		getKeyedCopyText(keyedCopy, CTA_COPY.primary.stepTag.key) ||
		CTA_COPY.primary.stepTag.fallback
	);
}

/**
 * Description text call to action
 */
export function getPrimaryCTABody(
	offering: FullCourseOffering,
	courseTitle: string,
	courseOfferings: FullCourseOfferings,
	keyedCopy: KeyedCopyEntry[] = [],
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"],
	campuses: CampusLocation[] = [],
	selectedCampusId: string | null = null
) {
	// Special case for versions sequence type
	if (sequenceType === "versions") {
		return (
			getKeyedCopyText(keyedCopy, CTA_COPY.primary.body.versions.key) ||
			CTA_COPY.primary.body.versions.fallback
		);
	}

	// Otherwise this is based on modality
	switch (offering.modality.slug.current) {
		case "in-person": {
			const selectedCampus = campuses.find(
				(campus) => campus.campus_id === selectedCampusId
			);

			// Body when there is no selected campus (Should not happen)
			if (!selectedCampus) {
				return (
					getKeyedCopyText(keyedCopy, CTA_COPY.primary.body.inPerson.key) ||
					CTA_COPY.primary.body.inPerson.fallback
				);
			}

			// Body when there is a selected campus
			const bodyTemplate =
				getKeyedCopyText(
					keyedCopy,
					CTA_COPY.primary.body.inPersonWithCampus.key
				) || CTA_COPY.primary.body.inPersonWithCampus.fallback;
			return bodyTemplate.replace("{{campusName}}", selectedCampus.short_name);
		}
		case "video-based":
		case "chat-based":
		case "self-paced": {
			// If there is no track, return a special message
			if (!offering.track || offering.track.slug.current === "track-none") {
				return (
					getKeyedCopyText(keyedCopy, CTA_COPY.primary.body.noTrack.key) ||
					CTA_COPY.primary.body.noTrack.fallback
				);
			}

			// Otherwise, return the track based body text
			return getTrackBasedPrimaryCTABody(
				offering,
				courseTitle,
				courseOfferings,
				keyedCopy
			);
		}
		default: {
			return "";
		}
	}
}

/**
 * Offerings that share modality with the primary CTA, and sequence label unless
 * this is a versions course (all versions are considered together).
 */
function matchesPrimaryCTAOffering(
	candidate: FullCourseOffering,
	offering: FullCourseOffering,
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"]
) {
	const matchesModality = candidate.modality._id === offering.modality._id;
	const matchesSequence =
		sequenceType === "versions" ||
		candidate.sequenceLabel === offering.sequenceLabel;
	return matchesModality && matchesSequence;
}

/**
 * Tells what type of class this is
 */
export function getPrimaryCTATuitionHeader(
	offering: FullCourseOffering,
	keyedCopy: KeyedCopyEntry[] = [],
	courseOfferings: FullCourseOfferings = [],
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"]
) {
	// If any matching offering has a different price, show the differing prices header
	const hasDifferingPrices = courseOfferings.some(
		(candidate) =>
			matchesPrimaryCTAOffering(candidate, offering, sequenceType) &&
			candidate.price !== offering.price
	);
	const headerCopy = hasDifferingPrices
		? CTA_COPY.tuition.diffPricesHeader
		: CTA_COPY.tuition.header;
	const headerTemplate =
		getKeyedCopyText(keyedCopy, headerCopy.key) || headerCopy.fallback;
	return headerTemplate.replace(
		"{{modalityName}}",
		offering.modality.title.toUpperCase()
	);
}

/**
 * Lowest price among offerings that match the primary CTA offering
 */
export function getPrimaryCTATuition(
	offering: FullCourseOffering,
	displayMode: string | null | undefined,
	selectedCampusId: string | null,
	campusCoursePrices: Readonly<Record<string, number>>,
	courseOfferings: FullCourseOfferings = [],
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"]
) {
	// Get all matching offerings
	const matchingOfferings = courseOfferings.filter((candidate) =>
		matchesPrimaryCTAOffering(candidate, offering, sequenceType)
	);
	const offeringsToPrice =
		matchingOfferings.length > 0 ? matchingOfferings : [offering];

	// Find the lowest price
	let minPrice: number | null = null;
	for (const candidate of offeringsToPrice) {
		const price = getOfferingPrice(
			candidate,
			displayMode,
			selectedCampusId,
			campusCoursePrices
		);
		if (price === null) {
			continue;
		}
		if (minPrice === null || price < minPrice) {
			minPrice = price;
		}
	}

	return minPrice;
}

/**
 * Tells when we bill the price
 */
export function getPrimaryCTATuitionLabel(
	offering: FullCourseOffering,
	keyedCopy: KeyedCopyEntry[] = [],
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"],
	inPersonBillingType?: InPersonBillingType | null
) {
	let labelKey: keyof typeof CTA_COPY.tuition.label = "default";

	if (offering.modality.slug.current === "in-person") {
		if (resolveInPersonBillingType(inPersonBillingType) === "trimester") {
			labelKey = "inPerson";
		}
	} else if (sequenceType === "sequential") {
		labelKey = "sequential";
	}

	return (
		getKeyedCopyText(keyedCopy, CTA_COPY.tuition.label[labelKey].key) ||
		CTA_COPY.tuition.label[labelKey].fallback
	);
}

/**
 * Give a total price
 */
export function getPrimaryCTATuitionSubLabel(
	tuition: number | null,
	offering: FullCourseOffering,
	keyedCopy: KeyedCopyEntry[] = [],
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"],
	inPersonBillingType?: InPersonBillingType | null
) {
	if (tuition === null) {
		return "";
	}

	let sublabelKey: keyof typeof CTA_COPY.tuition.sublabel = "default";
	let multiplier = 1;

	if (offering.modality.slug.current === "in-person") {
		if (resolveInPersonBillingType(inPersonBillingType) === "trimester") {
			sublabelKey = "inPerson";
			multiplier = 3;
		}
	} else if (sequenceType === "sequential") {
		sublabelKey = "sequential";
		multiplier = 2;
	}

	const totalPrice = formatOfferingPrice(tuition * multiplier);
	const sublabelTemplate =
		getKeyedCopyText(keyedCopy, CTA_COPY.tuition.sublabel[sublabelKey].key) ||
		CTA_COPY.tuition.sublabel[sublabelKey].fallback;
	return sublabelTemplate.replace("{{totalPrice}}", totalPrice);
}

/**
 * The link for the call to action button
 */
export function getPrimaryCTAButtonLink(
	offering: FullCourseOffering,
	consultationUrl: string
) {
	switch (offering.modality.slug.current) {
		case "in-person": {
			return consultationUrl;
		}
		case "video-based":
		case "chat-based":
		case "self-paced": {
			return appendScheduleAnchor(
				offering.courseUrl,
				offering.modality.slug.current
			);
		}
		default: {
			return "";
		}
	}
}

/**
 * The primary CTA button only leads to the consultation form for in-person
 * offerings (see `getPrimaryCTAButtonLink`) — other modalities go straight to
 * `offering.courseUrl`, which is out of scope for Discovery event tracking.
 */
export function shouldTrackConsultationInitiated(
	offering: FullCourseOffering
): boolean {
	return offering.modality.slug.current === "in-person";
}

/**
 * Resolves which Discovery funnel event (if any) a primary CTA button click
 * should fire. Book-consultation is tracked with `source: "primary_cta"` and
 * gated by `shouldTrackConsultationInitiated`, distinct from the secondary
 * CTA's book-consultation tracking; every other slug defers to the shared
 * schedule-link resolver.
 *
 * `offering` is null for Beast Academy, which has no course offerings; its
 * button falls through to the schedule-link resolver like any other slug.
 */
export function resolvePrimaryCTAButtonTrackingEvent(
	slug: string,
	courseSlug: string,
	offering: FullCourseOffering | null
): ScheduleLinkTrackingEvent | null {
	if (slug === "book-consultation") {
		if (!offering || !shouldTrackConsultationInitiated(offering)) {
			return null;
		}
		return {
			event: "consultation_request_initiated",
			properties: {course_slug: courseSlug, source: "primary_cta"},
		};
	}

	return resolveScheduleLinkTrackingEvent(slug, courseSlug);
}

/**
 * The label for the call to action button
 */
export function getPrimaryCTAButtonLabel(
	offering: FullCourseOffering,
	keyedCopy: KeyedCopyEntry[] = []
) {
	switch (offering.modality.slug.current) {
		case "in-person": {
			return (
				getKeyedCopyText(keyedCopy, CTA_COPY.button.inPerson.key) ||
				CTA_COPY.button.inPerson.fallback
			);
		}
		case "video-based":
		case "chat-based":
		case "self-paced": {
			if (
				offering.track?.title &&
				offering.track.slug.current !== "track-none"
			) {
				const buttonTemplate =
					getKeyedCopyText(keyedCopy, CTA_COPY.button.track.key) ||
					CTA_COPY.button.track.fallback;
				return buttonTemplate.replace("{{trackName}}", offering.track.title);
			}

			return (
				getKeyedCopyText(keyedCopy, CTA_COPY.button.noTrack.key) ||
				CTA_COPY.button.noTrack.fallback
			);
		}
		default: {
			return "";
		}
	}
}

/**
 * Buttons for the primary CTA action area.
 *
 * In-person keeps a single consultation button, including when the course uses
 * a versions sequence. Online and non-in-person versions show every matching
 * schedule link, including the selected offering.
 */
export function getPrimaryCTAButtons({
	offering,
	courseOfferings,
	campusIdForUrl,
	consultationUrl,
	keyedCopy = [],
	sequenceType,
}: {
	offering: FullCourseOffering;
	courseOfferings: FullCourseOfferings;
	campusIdForUrl: string | null;
	consultationUrl: string;
	keyedCopy?: KeyedCopyEntry[];
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"];
}): SecondaryCTAScheduleLink[] {
	// In-person uses a single consultation button.
	if (offering.modality.slug.current === "in-person") {
		return [
			{
				href: getPrimaryCTAButtonLink(offering, consultationUrl),
				label: getPrimaryCTAButtonLabel(offering, keyedCopy),
				description: offering.track?.title ?? offering.title,
				slug: "book-consultation",
			},
		];
	}

	// All others show the schedule links.
	return getCourseOfferingScheduleLinks({
		courseOfferings,
		offering,
		campusIdForUrl,
		keyedCopy,
		sequenceType,
	});
}

export type Modality = NonNullable<
	NonNullable<FullCourseLevel>["modalityOfferings"]
>[number]["modality"];

/** Everything the primary CTA renders, however it was resolved. */
export interface PrimaryCTAContent {
	stepTag: string;
	header: string;
	/** Empty when the variant has no subtitle. */
	body: string;
	tuitionHeader: string;
	tuition: number | null;
	tuitionLabel: string;
	tuitionSubLabel: string;
	buttons: SecondaryCTAScheduleLink[];
}

/** Primary CTA content for a course offering (in-person and online modalities). */
export function getOfferingPrimaryCTAContent({
	offering,
	courseOfferings,
	courseTitle,
	keyedCopy = [],
	campuses = [],
	campusCoursePrices,
	campusIdForUrl,
	consultationUrl,
	priceDisplayMode,
	selectedCampusId,
	sequenceType,
	inPersonBillingType,
}: {
	offering: FullCourseOffering;
	courseOfferings: FullCourseOfferings;
	courseTitle: string;
	keyedCopy?: KeyedCopyEntry[];
	campuses?: CampusLocation[];
	campusCoursePrices: Readonly<Record<string, number>>;
	campusIdForUrl: string | null;
	consultationUrl: string;
	priceDisplayMode: string | null | undefined;
	selectedCampusId: string | null;
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"];
	inPersonBillingType?: InPersonBillingType | null;
}): PrimaryCTAContent {
	const tuition = getPrimaryCTATuition(
		offering,
		priceDisplayMode,
		selectedCampusId,
		campusCoursePrices,
		courseOfferings,
		sequenceType
	);

	return {
		stepTag: getPrimaryCTAStepTag(keyedCopy),
		header: getPrimaryCTAHeader(
			offering,
			campuses,
			selectedCampusId,
			keyedCopy,
			sequenceType,
			courseTitle
		),
		body: getPrimaryCTABody(
			offering,
			courseTitle,
			courseOfferings,
			keyedCopy,
			sequenceType,
			campuses,
			selectedCampusId
		),
		tuitionHeader: getPrimaryCTATuitionHeader(
			offering,
			keyedCopy,
			courseOfferings,
			sequenceType
		),
		tuition,
		tuitionLabel: getPrimaryCTATuitionLabel(
			offering,
			keyedCopy,
			sequenceType,
			inPersonBillingType
		),
		tuitionSubLabel: getPrimaryCTATuitionSubLabel(
			tuition,
			offering,
			keyedCopy,
			sequenceType,
			inPersonBillingType
		),
		buttons: getPrimaryCTAButtons({
			offering,
			courseOfferings,
			campusIdForUrl,
			consultationUrl,
			keyedCopy,
			sequenceType,
		}),
	};
}

/**
 * Primary CTA content for Beast Academy.
 *
 * Beast Academy has no course offerings, so price comes from the modality's
 * manual price fields and the button sends visitors to Beast Academy Online.
 */
export function getBeastAcademyPrimaryCTAContent(
	modality: Modality,
	keyedCopy: KeyedCopyEntry[] = []
): PrimaryCTAContent {
	const price = modality?.price;

	return {
		stepTag: getPrimaryCTAStepTag(keyedCopy),
		header:
			getKeyedCopyText(keyedCopy, CTA_COPY.beastAcademy.header.key) ||
			CTA_COPY.beastAcademy.header.fallback,
		body: "",
		tuitionHeader:
			getKeyedCopyText(keyedCopy, CTA_COPY.beastAcademy.tuitionHeader.key) ||
			CTA_COPY.beastAcademy.tuitionHeader.fallback,
		tuition: typeof price?.amount === "number" ? price.amount : null,
		tuitionLabel: price?.period ?? "",
		tuitionSubLabel: price?.footnote ?? "",
		buttons: [
			{
				href: BEAST_ACADEMY_ENROLL_URL,
				label:
					getKeyedCopyText(keyedCopy, CTA_COPY.beastAcademy.button.key) ||
					CTA_COPY.beastAcademy.button.fallback,
				description: modality?.title ?? "",
				slug: BEAST_ACADEMY_MODALITY_SLUG,
			},
		],
	};
}

/** Tag label for the disabled (no selection) primary CTA. */
export function getDisabledPrimaryCTATag(keyedCopy: KeyedCopyEntry[] = []) {
	return getPrimaryCTAStepTag(keyedCopy);
}

/** Header for the disabled (no selection) primary CTA. */
export function getDisabledPrimaryCTAHeader(keyedCopy: KeyedCopyEntry[] = []) {
	return (
		getKeyedCopyText(keyedCopy, CTA_COPY.disabled.header.key) ||
		CTA_COPY.disabled.header.fallback
	);
}

/** Body/subtitle for the disabled (no selection) primary CTA. Optional — empty if unset. */
export function getDisabledPrimaryCTABody(keyedCopy: KeyedCopyEntry[] = []) {
	return getKeyedCopyText(keyedCopy, CTA_COPY.disabled.body.key);
}

/** Tuition-panel eyebrow when no offering is selected. */
export function getDisabledPrimaryCTATuitionHeader(
	keyedCopy: KeyedCopyEntry[] = []
) {
	return (
		getKeyedCopyText(keyedCopy, CTA_COPY.disabled.tuitionHeader.key) ||
		CTA_COPY.disabled.tuitionHeader.fallback
	);
}

/** Tuition-panel placeholder when no offering is selected. */
export function getDisabledPrimaryCTATuitionPlaceholder(
	keyedCopy: KeyedCopyEntry[] = []
) {
	return (
		getKeyedCopyText(keyedCopy, CTA_COPY.disabled.tuitionPlaceholder.key) ||
		CTA_COPY.disabled.tuitionPlaceholder.fallback
	);
}

/** Button label for the disabled (no selection) primary CTA. */
export function getDisabledPrimaryCTAButtonLabel(
	keyedCopy: KeyedCopyEntry[] = []
) {
	return (
		getKeyedCopyText(keyedCopy, CTA_COPY.disabled.button.key) ||
		CTA_COPY.disabled.button.fallback
	);
}

/**
 * Track based body text describes the tracks offered for the course
 */
export function getTrackBasedPrimaryCTABody(
	offering: FullCourseOffering,
	courseTitle: string,
	courseOfferings: FullCourseOfferings,
	keyedCopy: KeyedCopyEntry[] = []
) {
	// Get the matching tracks for the course
	const matchingTracks = new Map(
		courseOfferings
			.filter(
				(candidate) =>
					candidate.modality._id === offering.modality._id &&
					candidate.sequenceLabel === offering.sequenceLabel
			)
			.map((candidate) => [
				candidate.track._id,
				{
					slug: candidate.track.slug.current,
					title: candidate.track.title,
				},
			])
	);

	// Get the track names in the order of the track order
	const trackOrder = ["honors", "scholars"];
	const trackNames = [...matchingTracks.values()]
		.sort((a, b) => trackOrder.indexOf(a.slug) - trackOrder.indexOf(b.slug))
		.map(({title}) => title);
	const [trackName] = trackNames;

	// If there is only one track, return the course title and the track name
	if (trackNames.length === 1 && trackName) {
		const bodyTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.primary.body.oneTrack.key) ||
			CTA_COPY.primary.body.oneTrack.fallback;
		return bodyTemplate
			.replace("{{courseTitle}}", courseTitle)
			.replace("{{trackName}}", trackName);
	}

	// If there are multiple tracks, return the course title and the track names
	if (trackNames.length > 1) {
		// Get track names as a string
		const lastTrackName = trackNames[trackNames.length - 1];
		const precedingTrackNames = trackNames.slice(0, -1).join(", ");
		const trackNamesString = `${precedingTrackNames} and ${lastTrackName}`;

		// Get the body template
		const bodyTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.primary.body.multiTrack.key) ||
			CTA_COPY.primary.body.multiTrack.fallback;
		return bodyTemplate
			.replace("{{courseTitle}}", courseTitle)
			.replace("{{trackNames}}", trackNamesString);
	}

	return "";
}
